import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  useTheme,
  Divider,
  alpha,
  Typography,
  Collapse,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  Dashboard,
  People,
  Assignment,
  School,
  Engineering,
  AutoStories,
  ManageAccounts,
  Assessment,
  Payment,
  Lock,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  SwapHorizontalCircle,
  Category,
  Business,
  Domain,
  AccountTree,
  SchoolOutlined,
} from "@mui/icons-material";
import kaasLogo from "../Images/Vetrina _Logo.png";
import kaasNightLogo from "../Images/KaasNight.png";
import { useColorMode } from "../../../theme/ThemeContext";

const drawerWidth = 280;
const collapsedWidth = 80;
const GOLD = "#D4AF37";

const Sidebar = ({ activeMenu, setActiveMenu, isOpen, toggleSidebar }) => {
  const theme = useTheme();
  const location = useLocation();
  const { mode } = useColorMode();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: <Dashboard /> },
    { id: "user-management", label: "User Management", icon: <People /> },
    {
      id: "admin-master",
      label: "Master",
      icon: <Category />,
      subItems: [
        { id: "create-category", label: "Create Category", icon: <Category /> },
        { id: "create-occupation", label: "Create Occupation", icon: <Engineering /> },
        { id: "create-education", label: "Create Education", icon: <School /> },
        { id: "create-department", label: "Create Department", icon: <AccountTree /> },
        { id: "create-designation", label: "Create Designation", icon: <ManageAccounts /> },
        { id: "create-division", label: "Create Division", icon: <Business /> },
        { id: "create-subdivision", label: "Create Subdivision", icon: <Domain /> },
      ],
    },
    { id: "course-assignment", label: "Course Assign", icon: <Assignment /> },
    { id: "instructor-management", label: "Instructor Management", icon: <Engineering /> },
    { id: "course-approval", label: "Course Creation", icon: <AutoStories /> },
    { id: "group-management", label: "Group Management", icon: <ManageAccounts /> },
    { id: "reports-analytics", label: "Reports & Analytics", icon: <Assessment /> },
    { id: "billing-payments", label: "Billing & Payments", icon: <Payment /> },
    { id: "access-control", label: "Access Control", icon: <Lock /> },
    {
      id: "project-nav",
      label: "Switch Projects",
      icon: <SwapHorizontalCircle />,
      subItems: [
        { id: "aaas-project", label: "AaaS Platform", icon: <SchoolOutlined />, url: "/db-shiksha/aaas/", external: true },
        { id: "gatep-project", label: "GateP Platform", icon: <SchoolOutlined />, url: "/db-shiksha/gatep/", external: true },
      ],
    },
  ];

  const handleToggleSubMenu = (id) => {
    setExpandedMenu(expandedMenu === id ? null : id);
  };

  const menuStyle = (isActive) => ({
    minHeight: 48,
    justifyContent: isOpen ? "initial" : "center",
    px: 2.5,
    mx: 1,
    borderRadius: "12px",
    mb: 0.5,
    transition: "all 0.2s ease",
    bgcolor: isActive ? alpha(GOLD, 0.1) : "transparent",
    color: isActive ? GOLD : "text.secondary",
    "&:hover": {
      bgcolor: isActive ? alpha(GOLD, 0.15) : alpha(theme.palette.text.primary, 0.04),
      color: GOLD,
      "& .MuiListItemIcon-root": { color: GOLD },
    },
  });

  return (
    <Box
      sx={{
        width: isOpen ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        boxSizing: "border-box",
        position: "relative",
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Drawer
        variant="permanent"
        open={isOpen}
        sx={{
          "& .MuiDrawer-paper": {
            width: isOpen ? drawerWidth : collapsedWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: alpha(GOLD, 0.1),
            height: "100vh",
            zIndex: 1201,
          },
        }}
      >
        {/* Header / Logo Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: isOpen ? "space-between" : "center",
            px: 2,
            minHeight: 64,
            mb: 1
          }}
        >
          {isOpen && (
            <Box
              component="img"
              src={mode === "dark" ? kaasNightLogo : kaasLogo}
              sx={{ height: 32, width: "auto", cursor: "pointer" }}
              onClick={() => (window.location.href = "/db-shiksha/kaas/admin/dashboard")}
            />
          )}
          <IconButton onClick={toggleSidebar} sx={{ color: GOLD }}>
            {isOpen ? <ChevronLeft /> : <ChevronRight />}
          </IconButton>
        </Box>
        
        <Divider sx={{ mb: 1, opacity: 0.5 }} />

        <List sx={{ px: 1 }}>
          {menuItems.map((item) => {
            const isActive = activeMenu === item.id || (item.subItems && item.subItems.some(sub => activeMenu === sub.id));
            const isExpanded = expandedMenu === item.id;

            return (
              <React.Fragment key={item.id}>
                <ListItem disablePadding sx={{ display: "block" }}>
                  <Tooltip title={!isOpen ? item.label : ""} placement="right">
                    <ListItemButton
                      onClick={() => item.subItems ? handleToggleSubMenu(item.id) : setActiveMenu(item.id)}
                      sx={menuStyle(isActive)}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isOpen ? 2 : "auto",
                          justifyContent: "center",
                          color: isActive ? GOLD : "inherit",
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          opacity: isOpen ? 1 : 0,
                          "& .MuiTypography-root": {
                            fontWeight: isActive ? 700 : 500,
                            fontSize: "0.9rem",
                            fontFamily: "'Outfit', sans-serif"
                          },
                        }}
                      />
                      {isOpen && item.subItems && (isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />)}
                      {isActive && isOpen && !item.subItems && (
                        <Box sx={{ width: 4, height: 18, bgcolor: GOLD, borderRadius: 2, ml: 1 }} />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {item.subItems && (
                  <Collapse in={isExpanded && isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ mb: 1 }}>
                      {item.subItems.map((sub) => {
                        const isSubActive = activeMenu === sub.id;
                        
                        const handleProjectClick = async (e) => {
                          if (!sub.external) {
                            setActiveMenu(sub.id);
                            return;
                          }
                          
                          if (sub.id === "gatep-project") {
                            e.preventDefault();
                            setIsSyncing(true);
                            const token = localStorage.getItem("jwttoken");
                            const role = localStorage.getItem("role");
                            
                            if (token) {
                              try {
                                const response = await fetch("https://tdtlworld.com/dss-gatep-backend/api/sso-sync/", {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ token })
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  localStorage.setItem("access_token", data.access);
                                  localStorage.setItem("refresh_token", data.refresh);
                                  const gatepUserData = {
                                    username: data.username,
                                    email: data.email,
                                    role: data.user_role.toLowerCase(),
                                  };
                                  localStorage.setItem("gatep_user", JSON.stringify(gatepUserData));
                                  window.location.href = "/db-shiksha/gatep/admin";
                                  return;
                                }
                              } catch (err) {
                                console.error("SSO sync failed:", err);
                              } finally {
                                setIsSyncing(false);
                              }
                            }
                          }
                          window.location.href = sub.url;
                        };

                        return (
                          <ListItemButton
                            key={sub.id}
                            onClick={handleProjectClick}
                            sx={{
                              pl: 4,
                              minHeight: 40,
                              mx: 1,
                              borderRadius: "10px",
                              color: isSubActive ? GOLD : "text.secondary",
                              bgcolor: isSubActive ? alpha(GOLD, 0.05) : "transparent",
                              "&:hover": { color: GOLD, bgcolor: alpha(GOLD, 0.05) },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 0, mr: 2, color: isSubActive ? GOLD : "inherit", fontSize: 18 }}>
                              {sub.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={sub.label}
                              primaryTypographyProps={{
                                fontSize: "0.85rem",
                                fontWeight: isSubActive ? 700 : 500,
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>

        <Box sx={{ flexGrow: 1 }} />
        
        {isOpen && (
          <Box sx={{ p: 2, textAlign: "center", opacity: 0.5 }}>
            <Typography variant="caption" fontWeight={600}>
              © 2024 Admin Portal
            </Typography>
          </Box>
        )}
      </Drawer>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1202, display: 'flex', flexDirection: 'column', gap: 2 }}
        open={isSyncing}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Syncing with GateP Platform...
        </Typography>
      </Backdrop>
    </Box>
  );
};

export default Sidebar;
